import React from 'react';

import { Inject } from '@odinjs/odin';

import { EQUALS, equalsValidator } from '@philips-emr/hpms-components';
import { FieldAPI, FormAPI, InputAPI, LinkAPI, PanelAPI, TextAPI, WizardAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange } from '@philips-emr/tws-constants';
import Controller, { OnAfterNavigate, OnBeforeSubmit, OnChange, OnMount, OnValidate } from '@philips-emr/tws-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, MountEvent, NavigateEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { getFormatter } from '@philips-emr/tws-core/lib/services/model-service';

import { FragmentNames, ModalCadastrarNovoUsuarioAliases } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { ToastService } from '../../services';

enum Step {
  INFORMACOES = 'informacoes',
  DADOS = 'dados',
}

const ALPHA_NUMERIC = 'alphaNumeric';
const PERSON_ID = 'person-id';
const VALUE = 'value';

@Controller({ fragment: FragmentNames.MODAL_CADASTRAR_NOVO_USUARIO })
export default class NovoUsuarioController {

  @Inject(ModalCadastrarNovoUsuarioAliases.PANEL_WIZARD)
  private panelWizard: PanelAPI;
  @Inject(ModalCadastrarNovoUsuarioAliases.PANEL_AVISO)
  private panelAviso: PanelAPI;
  @Inject(ModalCadastrarNovoUsuarioAliases.WIZARD)
  private wizard: WizardAPI;
  @Inject(ModalCadastrarNovoUsuarioAliases.FORM_INFORMACOES)
  private formInformacoes: FormAPI;
  @Inject(ModalCadastrarNovoUsuarioAliases.FORM_DADOS)
  private formDados: FormAPI;

  @OnBeforeSubmit(ModalCadastrarNovoUsuarioAliases.WIZARD)
  onBeforeSubmit({ abort }: AbortableSubmitEvent): void {
    this.setReadOnly(true);

    const isSubmittable: Promise<boolean> = HttpService.post<{}>(Endpoints.POST.NOVO_USUARIO, {}, { body: this.getBody() })
      .then((response) => {
        if (response.body) {
          this.panelWizard.setVisible(false);
          this.panelAviso.setVisible(true);
        }
        return false;
      })
      .catch((response) => {
        ToastService.open(response);
        this.setReadOnly(false);
        return true;
      });

    abort(isSubmittable);
  }

  @OnValidate(ModalCadastrarNovoUsuarioAliases.FORM_INFORMACOES)
  onFormInformacoesValidate = ({ valid }: ValidateEvent): void => this.wizard.setStepDone(Step.INFORMACOES, valid);

  @OnValidate(ModalCadastrarNovoUsuarioAliases.FORM_DADOS)
  onFormDadosValidate = ({ valid }: ValidateEvent): void => this.wizard.setStepDone(Step.DADOS, valid);

  @OnAfterNavigate(ModalCadastrarNovoUsuarioAliases.WIZARD)
  onAfterNavigate({ to }: NavigateEvent<Step>): void {
    if (to === Step.DADOS) {
      const record = this.formDados.getRecord();
      if (Object.keys(record).length) {
        this.formDados.validate();
      }
    }
  }

  @OnChange(ModalCadastrarNovoUsuarioAliases.INPUT_CPF_RG)
  onInputCpfRgChange({ target, value }: ChangeEvent<string>): void {
    const api = target as InputAPI;
    const formatter = getFormatter(PERSON_ID).validate(value)[0];

    api.removeFormatterByProperty(VALUE);
    api.setFormatterByProperty(VALUE, formatter.valid ? PERSON_ID : ALPHA_NUMERIC);
  }

  @OnMount(ModalCadastrarNovoUsuarioAliases.RESULT_INFORMACOES)
  onMount({ target }: MountEvent): void {
    const api = target as TextAPI;
    const { cardNumber } = this.formInformacoes.getRecord();
    cardNumber && api.setValue(`${MessageService.translate('beneficiary.carteira')}: ${cardNumber}`);
  }

  @OnMount(ModalCadastrarNovoUsuarioAliases.FIELD_CONFIRMAR_EMAIL)
  onMountFieldEmail({ target }: MountEvent): void {
    const api = target as FieldAPI;
    api.addLocalValidator(EQUALS, (value) => {
      const { email } = this.formDados.getRecord();
      return equalsValidator(value as string, email as string);
    });
  }

  @OnMount(ModalCadastrarNovoUsuarioAliases.FIELD_CONFIRMAR_SENHA)
  onMountFieldSenha({ target }: MountEvent): void {
    const api = target as FieldAPI;
    api.addLocalValidator(EQUALS, (value) => {
      const { password } = this.formDados.getRecord();
      return equalsValidator(value as string, password as string);
    });
  }

  @OnMount(ModalCadastrarNovoUsuarioAliases.LINK_TERMOS)
  onLinKTermosMount = (event: MountEvent): void => this.checkTermo(Endpoints.GET.TERMOS_USO, event);

  @OnMount(ModalCadastrarNovoUsuarioAliases.LINK_AVISO)
  onLinKAvisoMount = (event: MountEvent): void => this.checkTermo(Endpoints.GET.AVISO_PRIVACIDADE, event);

  private checkTermo(endpoint: string, event: MountEvent): void {
    const api = event.target as LinkAPI;

    void HttpService.get<{ nrSeqTermo: number }>(endpoint).then(response => {
      if (response.body) {
        const { nrSeqTermo } = response.body;

        if (nrSeqTermo) {
          api.setEnabled(true);
          api.onClick.listen(() => this.openTermo(endpoint, nrSeqTermo));
        }
      }
    });
  }

  private openTermo(endpoint: string, nrSeqTermo: number): void {
    void HttpService.get<{ termo: string }>(`${endpoint}/:nrSeqTermo`, { nrSeqTermo })
      .then(response => {
        if (response.body) {
          const { termo } = response.body;
          ModalService.openDefault({
            closeable: true,
            theme: ThemeTonalRange.ULTRA_LIGHT,
            children: (<div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: termo }} />),
          });
        }
      })
      .catch(response => ToastService.open(response));
  }

  private setReadOnly(value: boolean): void {
    this.wizard.setReadOnly(value);
    this.formDados.setState({ editing: !value });
  }

  private getBody(): {} {
    const { cardNumber: carteira, cpfRgNumber, birthDate: dtNascimento } = this.formInformacoes.getRecord();
    const formatter = getFormatter(PERSON_ID).validate(cpfRgNumber)[0];

    return {
      ...this.formDados.getRecord(),
      carteira,
      dtNascimento,
      nrCpf: formatter.valid ? cpfRgNumber : '',
      nrRg: !formatter.valid ? cpfRgNumber : '',
    };
  }
}
